import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import Tabs from '~/components/Tabs';
import {
    approveUnmonitoredChange,
    bulkApproveUnmonitoredChanges,
    bulkDeleteUnmonitoredChanges,
    deleteUnmonitoredChange,
    fetchUnmonitoredChanges,
} from '~/services/unmonitored-changes';
import type {
    PaginatedUnmonitoredChanges,
    UnmonitoredChange,
    UnmonitoredChangeType,
} from '~/services/unmonitored-changes';

type TabKey = 'all' | UnmonitoredChangeType;

const TYPE_LABELS: Record<UnmonitoredChangeType, string> = {
    guest_book: 'Guest Book',
    story_change: 'Story Change',
    photo_update: 'Photo Update',
};

const TABS: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'guest_book', label: 'Guest Book' },
    { key: 'story_change', label: 'Story Change' },
    { key: 'photo_update', label: 'Photo Update' },
];

const formatDateParts = (iso: string) => {
    const d = new Date(iso);
    const date = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(
        d.getDate()
    ).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const time = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    return { date, time };
};

const PAGE_SIZE = 20;

const UnMonitoredChanges = () => {
    const [tab, setTab] = useState<TabKey>('all');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<PaginatedUnmonitoredChanges | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [busy, setBusy] = useState(false);
    const [rowBusy, setRowBusy] = useState<Map<number, 'publish' | 'delete'>>(
        new Map()
    );
    const cacheRef = useRef(
        new Map<string, PaginatedUnmonitoredChanges>()
    );
    const cacheKey = `${tab}:${page}`;

    const setRowBusyFor = (id: number, kind: 'publish' | 'delete' | null) => {
        setRowBusy((prev) => {
            const next = new Map(prev);
            if (kind === null) next.delete(id);
            else next.set(id, kind);
            return next;
        });
    };

    const invalidateOtherCaches = () => {
        for (const key of Array.from(cacheRef.current.keys())) {
            if (key !== cacheKey) cacheRef.current.delete(key);
        }
    };

    const updateRowInPlace = (
        id: number,
        patch: Partial<UnmonitoredChange>
    ) => {
        setData((prev) => {
            if (!prev) return prev;
            const next = {
                ...prev,
                results: prev.results.map((r) =>
                    r.id === id ? { ...r, ...patch } : r
                ),
            };
            cacheRef.current.set(cacheKey, next);
            invalidateOtherCaches();
            return next;
        });
    };

    const removeRowInPlace = (id: number) => {
        setData((prev) => {
            if (!prev) return prev;
            const removed = prev.results.find((r) => r.id === id);
            if (!removed) return prev;
            const next = {
                ...prev,
                results: prev.results.filter((r) => r.id !== id),
                total: Math.max(0, prev.total - 1),
                counts: {
                    ...prev.counts,
                    all: Math.max(0, prev.counts.all - 1),
                    [removed.type]: Math.max(
                        0,
                        prev.counts[removed.type] - 1
                    ),
                },
            };
            cacheRef.current.set(cacheKey, next);
            invalidateOtherCaches();
            return next;
        });
    };

    const load = useCallback(() => {
        const key = `${tab}:${page}`;
        const cached = cacheRef.current.get(key);
        if (cached) {
            setData(cached);
            setIsLoading(false);
            setError(null);
            return;
        }
        setIsLoading(true);
        setError(null);
        fetchUnmonitoredChanges({ type: tab, page, page_size: PAGE_SIZE })
            .then((d) => {
                cacheRef.current.set(key, d);
                setData(d);
            })
            .catch((e) => setError(e?.message ?? 'Failed to load'))
            .finally(() => setIsLoading(false));
    }, [tab, page]);

    const rowById = useMemo(() => {
        const map = new Map<number, UnmonitoredChange>();
        (data?.results ?? []).forEach((r) => map.set(r.id, r));
        return map;
    }, [data]);

    useEffect(() => {
        load();
    }, [load]);

    const rows: UnmonitoredChange[] = useMemo(
        () => data?.results ?? [],
        [data]
    );

    useEffect(() => {
        if (selectedIds.size === 0) return;
        const visible = new Set(rows.map((r) => r.id));
        const next = new Set<number>();
        let changed = false;
        selectedIds.forEach((id) => {
            if (visible.has(id)) next.add(id);
            else changed = true;
        });
        if (changed) setSelectedIds(next);
    }, [rows, selectedIds]);

    const allVisibleSelected =
        rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
    const someSelected = selectedIds.size > 0;

    const toggleSelectAll = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                rows.forEach((r) => next.delete(r.id));
            } else {
                rows.forEach((r) => next.add(r.id));
            }
            return next;
        });
    };

    const toggleRow = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleApprove = async (row: UnmonitoredChange) => {
        setRowBusyFor(row.id, 'publish');
        try {
            await approveUnmonitoredChange(row);
            if (row.type === 'guest_book') {
                updateRowInPlace(row.id, { status: 'published' });
            } else {
                removeRowInPlace(row.id);
            }
        } finally {
            setRowBusyFor(row.id, null);
        }
    };

    const handleDelete = async (row: UnmonitoredChange) => {
        setRowBusyFor(row.id, 'delete');
        try {
            await deleteUnmonitoredChange(row);
            removeRowInPlace(row.id);
            setSelectedIds((prev) => {
                if (!prev.has(row.id)) return prev;
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
        } finally {
            setRowBusyFor(row.id, null);
        }
    };

    const selectedRows = useMemo(
        () =>
            Array.from(selectedIds)
                .map((id) => rowById.get(id))
                .filter((r): r is UnmonitoredChange => Boolean(r)),
        [selectedIds, rowById]
    );

    const handleBulkApprove = async () => {
        if (selectedRows.length === 0) return;
        setBusy(true);
        selectedRows.forEach((r) => setRowBusyFor(r.id, 'publish'));
        try {
            await bulkApproveUnmonitoredChanges(selectedRows);
            selectedRows.forEach((r) => {
                if (r.type === 'guest_book') {
                    updateRowInPlace(r.id, { status: 'published' });
                } else {
                    removeRowInPlace(r.id);
                }
            });
            setSelectedIds(new Set());
        } finally {
            selectedRows.forEach((r) => setRowBusyFor(r.id, null));
            setBusy(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        setBusy(true);
        selectedRows.forEach((r) => setRowBusyFor(r.id, 'delete'));
        try {
            await bulkDeleteUnmonitoredChanges(selectedRows);
            selectedRows.forEach((r) => removeRowInPlace(r.id));
            setSelectedIds(new Set());
        } finally {
            selectedRows.forEach((r) => setRowBusyFor(r.id, null));
            setBusy(false);
        }
    };

    const counts = data?.counts;
    const total = data?.total ?? 0;
    const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeTo = Math.min(page * PAGE_SIZE, total);
    const hasNext = page * PAGE_SIZE < total;
    const hasPrev = page > 1;

    const tabCount = (key: TabKey) => {
        if (!counts) return null;
        if (key === 'all') return counts.all;
        return counts[key];
    };

    return (
        <>
            <Breadcrumb pageName='Unmonitored Changes' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='px-4 pb-4 md:px-8'>
                    <Tabs
                        className='!mb-0'
                        openTab={TABS.findIndex((t) => t.key === tab) + 1}
                        setOpenTab={(index) => {
                            const next = TABS[index - 1];
                            if (!next) return;
                            setTab(next.key);
                            setPage(1);
                            setSelectedIds(new Set());
                        }}
                        tabNames={TABS.map((t) => ({
                            name: t.label,
                            count: tabCount(t.key) ?? undefined,
                        }))}
                    />
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-4 py-2 md:px-8'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        {isLoading
                            ? 'Loading…'
                            : total === 0
                              ? 'Showing 0 of 0 results'
                              : `Showing ${rangeFrom}–${rangeTo} of ${total} results`}
                    </p>

                    <div className='flex items-center gap-3'>
                        <span>
                            {someSelected
                                ? `${selectedIds.size} selected`
                                : ''}
                        </span>
                        <button
                            type='button'
                            onClick={handleBulkApprove}
                            disabled={!someSelected || busy}
                            className='rounded border border-primary px-3 py-1 text-xs font-medium text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {tab === 'guest_book'
                                ? 'Publish selected'
                                : 'Approve selected'}
                        </button>
                        <button
                            type='button'
                            onClick={handleBulkDelete}
                            disabled={!someSelected || busy}
                            className='rounded border border-danger px-3 py-1 text-xs font-medium text-danger transition hover:bg-danger hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            Delete selected
                        </button>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            <tr
                                className='border-t border-stroke bg-whiten'
                                style={{ fontSize: '14px' }}
                            >
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                        width: '48px',
                                    }}
                                >
                                    <input
                                        type='checkbox'
                                        aria-label='Select all'
                                        checked={allVisibleSelected}
                                        onChange={toggleSelectAll}
                                        disabled={rows.length === 0}
                                    />
                                </th>
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                >
                                    Type
                                </th>
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                >
                                    Date &amp; Time
                                </th>
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                >
                                    Campaign
                                </th>
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                >
                                    Update
                                </th>
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                        width: '110px',
                                    }}
                                >
                                    Status
                                </th>
                                <th
                                    className='text-right'
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                        width: '180px',
                                    }}
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className='px-3 py-12 text-center text-bodydark'
                                    >
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!isLoading && error && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className='px-3 py-12 text-center text-danger'
                                    >
                                        {error}
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !error && rows.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className='px-3 py-12 text-center text-bodydark'
                                    >
                                        No unmonitored changes.
                                    </td>
                                </tr>
                            )}
                            {!isLoading &&
                                !error &&
                                rows.map((row) => {
                                    const { date, time } = formatDateParts(
                                        row.created_at
                                    );
                                    return (
                                        <tr
                                            key={row.id}
                                            className='border-t border-stroke align-top'
                                        >
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                <input
                                                    type='checkbox'
                                                    aria-label={`Select row ${row.id}`}
                                                    checked={selectedIds.has(
                                                        row.id
                                                    )}
                                                    onChange={() =>
                                                        toggleRow(row.id)
                                                    }
                                                />
                                            </td>
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                {TYPE_LABELS[row.type]}
                                            </td>
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                <div>{date}</div>
                                                <div className='text-xs text-bodydark'>
                                                    {time}
                                                </div>
                                            </td>
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                <Link
                                                    className='font-bold text-primary'
                                                    to={`/campaigns/${row.campaign_id}`}
                                                >
                                                    {row.campaign_person_name}
                                                </Link>
                                            </td>
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                <div className='whitespace-pre-line text-xs leading-4'>
                                                    {row.body}
                                                </div>
                                                {row.signature && (
                                                    <div className='mt-1 text-xs italic text-bodydark'>
                                                        — {row.signature}
                                                    </div>
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                {row.status === 'pending' && (
                                                    <span className='inline-flex rounded-full bg-warning bg-opacity-10 px-2.5 py-0.5 text-xs font-medium text-warning'>
                                                        Pending
                                                    </span>
                                                )}
                                                {row.status === 'published' && (
                                                    <span className='inline-flex rounded-full bg-success bg-opacity-10 px-2.5 py-0.5 text-xs font-medium text-success'>
                                                        Published
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className='text-right'
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                            >
                                                <div className='flex justify-end gap-2'>
                                                    {row.status !==
                                                        'published' && (
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                handleApprove(
                                                                    row
                                                                )
                                                            }
                                                            disabled={
                                                                rowBusy.has(
                                                                    row.id
                                                                )
                                                            }
                                                            className='rounded border border-primary px-3 py-1 text-xs font-medium text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                                                        >
                                                            {rowBusy.get(
                                                                row.id
                                                            ) === 'publish'
                                                                ? row.type ===
                                                                  'guest_book'
                                                                    ? 'Publishing…'
                                                                    : 'Approving…'
                                                                : row.type ===
                                                                    'guest_book'
                                                                  ? 'Publish'
                                                                  : 'Approve'}
                                                        </button>
                                                    )}
                                                    <button
                                                        type='button'
                                                        onClick={() =>
                                                            handleDelete(row)
                                                        }
                                                        disabled={rowBusy.has(
                                                            row.id
                                                        )}
                                                        className='rounded border border-danger px-3 py-1 text-xs font-medium text-danger transition hover:bg-danger hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                                                    >
                                                        {rowBusy.get(
                                                            row.id
                                                        ) === 'delete'
                                                            ? 'Deleting…'
                                                            : 'Delete'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                <div className='flex flex-wrap items-center justify-between gap-3 border-t border-stroke px-4 py-3 md:px-8'>
                    <div
                        style={{ fontSize: '12px' }}
                    >{`Page ${page}`}</div>
                    <div className='flex gap-2'>
                        <button
                            type='button'
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={!hasPrev}
                            className='rounded border border-stroke px-3 py-1 text-xs transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            Previous
                        </button>
                        <button
                            type='button'
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!hasNext}
                            className='rounded border border-stroke px-3 py-1 text-xs transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            Next
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
};

export default UnMonitoredChanges;
